/**
 * Shared pieces. Small on purpose — a portal with a dozen screens does not
 * need a component library, and every abstraction here has several callers.
 *
 * Colours come from the page's section (index.css [data-sec]), so the same
 * hero, button or tile wears jobs-violet on Jobs and payments-green on
 * Payments without any screen passing a colour in.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { Icon, type IconName } from './Icon';

export function Card({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <section className={`card p-4 ${className}`}>{children}</section>;
}

/**
 * The top of a tab: a gradient banner with the page's name, one line on what
 * it's for, and up to three numbers that matter on it.
 */
export function PageHero({
  icon,
  eyebrow,
  title,
  sub,
  stats,
  action,
}: {
  icon: IconName;
  eyebrow?: string;
  title: string;
  sub?: string;
  stats?: Array<{ label: string; value: string }>;
  action?: ReactNode;
}) {
  return (
    <section
      className="relative mb-5 overflow-hidden rounded-[26px] p-5 text-white shadow-lift"
      style={{ backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }}
    >
      {/* Soft light shapes, only for depth. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full bg-white/15 blur-[2px]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-16 right-16 h-32 w-32 rounded-full bg-white/10"
      />
      <div className="relative flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20 ring-1 ring-white/30">
          <Icon name={icon} className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          {eyebrow && <p className="eyebrow text-white/75">{eyebrow}</p>}
          <h1 className="font-display text-[26px] font-bold leading-tight tracking-tight">
            {title}
          </h1>
          {sub && <p className="mt-1 text-sm leading-snug text-white/85">{sub}</p>}
        </div>
      </div>
      {stats && stats.length > 0 && (
        <dl className="relative mt-4 grid grid-flow-col auto-cols-fr gap-2">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl bg-white/15 px-3 py-2 ring-1 ring-white/20"
            >
              <dt className="truncate text-[11px] font-medium text-white/80">{s.label}</dt>
              <dd className="tnum font-display text-xl font-bold leading-tight">{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {action && <div className="relative mt-4">{action}</div>}
    </section>
  );
}

/** A page under a tab (Quote, Team…): a back link and a plain title. */
export function SubHeader({
  title,
  sub,
  back = '/profile',
  icon,
}: {
  title: string;
  sub?: string;
  back?: string | null;
  icon?: IconName;
}) {
  const { t } = useI18n();
  return (
    <div className="mb-4">
      {back && (
        <Link
          to={back}
          className="-ml-1 mb-2 inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold"
          style={{ color: 'var(--sec-ink)' }}
        >
          <Icon name="back" className="h-4 w-4" strokeWidth={2.4} />
          {t('common.back')}
        </Link>
      )}
      <div className="flex items-center gap-3">
        {icon && (
          <span className="icon-tile h-11 w-11">
            <Icon name={icon} className="h-6 w-6" />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="font-display text-[24px] font-bold leading-tight tracking-tight">
            {title}
          </h1>
          {sub && <p className="mt-0.5 text-sm text-muted">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

/** A section heading inside a page, with an optional count. */
export function SectionTitle({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <h2 className="mb-2.5 flex items-center gap-2 px-1 font-display text-[17px] font-bold">
      {children}
      {count !== undefined && (
        <span
          className="rounded-full px-2 py-0.5 text-xs font-bold"
          style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
        >
          {count}
        </span>
      )}
    </h2>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted">
      <span
        aria-hidden
        className="h-9 w-9 animate-spin rounded-full border-[3px] border-line"
        style={{ borderTopColor: 'var(--sec-ink)' }}
      />
      <span className="text-sm">{label ?? t('common.loading')}</span>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon = 'sparkle',
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="card px-6 py-12 text-center">
      <span
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl"
        style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
      >
        <Icon name={icon} className="h-8 w-8" />
      </span>
      <h2 className="mt-4 font-display text-lg font-bold">{title}</h2>
      {body && <p className="mx-auto mt-2 max-w-sm text-sm text-muted">{body}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useI18n();
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-offline/20 bg-offline/5 p-4">
      <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-bad" />
      <div>
        <p className="text-sm text-bad">{message}</p>
        {onRetry && (
          <button type="button" className="btn-quiet mt-1 text-sm" onClick={onRetry}>
            {t('common.retry')}
          </button>
        )}
      </div>
    </div>
  );
}

export type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-panel-2 text-muted',
  good: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
  warn: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  bad: 'bg-rose-50 text-rose-700 ring-rose-600/15',
  info: 'bg-violet-50 text-violet-700 ring-violet-600/15',
};
const DOTS: Record<Tone, string> = {
  neutral: 'bg-muted-dim',
  good: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-rose-500',
  info: 'bg-violet-500',
};

/** A status, with a dot in its colour (the dot pulses while it needs you). */
export function Badge({
  children,
  tone = 'neutral',
  pulse,
}: {
  children: ReactNode;
  tone?: Tone;
  pulse?: boolean;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ring-transparent ${TONES[tone]}`}
    >
      <span className="relative flex h-1.5 w-1.5">
        {pulse && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${DOTS[tone]}`}
          />
        )}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${DOTS[tone]}`} />
      </span>
      {children}
    </span>
  );
}

/** A small fact with an icon: "Meerut", "Due in 3 days". */
export function Meta({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-[13px] text-muted">
      <Icon name={icon} className="h-3.5 w-3.5 shrink-0" />
      {children}
    </span>
  );
}

/** A labelled money row. Used on payouts, where every line must be readable. */
export function MoneyRow({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <span className={`text-sm ${muted ? 'text-muted' : 'font-semibold'}`}>{label}</span>
      <span
        className={`tnum ${strong ? 'font-display text-xl font-bold' : ''} ${muted ? 'text-muted' : ''}`}
        style={strong ? { color: 'var(--sec-ink)' } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-bad">{error}</span>}
    </label>
  );
}

/**
 * A sticky action bar.
 *
 * The primary action on every screen sits here, in thumb reach, above the
 * safe-area inset (and above the tab bar, inside the app). A vendor should
 * never have to scroll to find the button that submits what they just typed.
 */
export function ActionBar({ children }: { children: ReactNode }) {
  return (
    <div className="action-bar safe-bottom glass sticky bottom-0 z-10 -mx-4 mt-6 rounded-t-3xl border-t border-white/70 px-4 pt-3">
      {children}
    </div>
  );
}
